import { useEffect, createContext, useContext, useMemo, useState, useRef } from 'react';
import config from '../config';
import { AuthStore } from '../stores/auth_store';
import { CompanySettingsStore } from '../stores/company_settings_store';
import { UserStore } from '../stores/user_store';
import { ConfigStore } from '../stores/config_store';
import { UIStore } from '../stores/ui_store';
import { SDKStore } from '../stores/sdk_store';

export interface GlobalContext {
    authStore: AuthStore;
    configStore: ConfigStore;
    userStore: UserStore;
    uiStore: UIStore;
    sdkStore: SDKStore;
    companySettingsStore: CompanySettingsStore;
}

const globalContext = createContext<GlobalContext | undefined>(undefined);

function createStores(): GlobalContext {
    const authStore = new AuthStore();
    const configStore = new ConfigStore();
    const sdkStore = new SDKStore();
    const companySettingsStore = new CompanySettingsStore(config.companyCode);
    const userStore = new UserStore(authStore, companySettingsStore);

    let uiStore: UIStore;
    if (window.navigator.cookieEnabled) {
        uiStore = new UIStore();
    }

    return {
        authStore,
        configStore,
        userStore,
        uiStore,
        sdkStore,
        companySettingsStore,
    };
}

export function useInitializeGlobalStores(): GlobalContext {
    // React ensures the initializer function only runs once, even in StrictMode
    const [stores] = useState(createStores());

    // prevents need for stores in dependency array
    const authStoreRef = useRef(stores.authStore);
    const configStoreRef = useRef(stores.configStore);
    const userStoreRef = useRef(stores.userStore);

    useEffect(() => {
        // stabilizes the ref within the useEffect to use in the deconstruction
        const userStore = userStoreRef.current;
        const authStore = authStoreRef.current;

        authStore.connect();
        configStoreRef.current.connect();
        userStore.connect();

        return () => {
            authStore.disconnect();
            userStore.disconnect();
        };
    }, []); // stores object is stable

    // Always return the same object reference
    return stores;
}

export function useGlobalStore(): GlobalContext {
    const context = useContext(globalContext);

    if (!context) {
        throw new Error('Unable to useGlobalStore without parent <GlobalStoreProvider />');
    }

    return context;
}

export function useAuthStore(): AuthStore {
    return useGlobalStore().authStore;
}

export function useUIStore(): UIStore {
    return useGlobalStore().uiStore;
}

export function useConfigStore(): ConfigStore {
    return useGlobalStore().configStore;
}

export function useUserStore(): UserStore {
    return useGlobalStore().userStore;
}

export function useSDKStore(): SDKStore {
    return useGlobalStore().sdkStore;
}

export function useCompanySettingsStore(): CompanySettingsStore {
    return useGlobalStore().companySettingsStore;
}

type StoreSelector<T> = (stores: GlobalContext) => T;

/**
 * Mimics a similar selector pattern as Redux
 * @param selector one of the store selectors
 * @returns the memoized selected store from the global context
 */
export function useStoreSelector<T>(selector: StoreSelector<T>): T {
    const stores = useGlobalStore();

    // Memoize selected value to prevent unnecessary re-renders
    return useMemo(() => selector(stores), [stores, selector]);
}

export const GlobalStoreProvider = globalContext.Provider;
